import React, { FC, useEffect, useRef } from 'react';

import {
  Input,
  Checkbox,
  InputGroup,
  InputRightElement,
  useDisclosure,
} from '@chakra-ui/react';
import { Flex, SimpleGrid, Text } from '@chakra-ui/layout';
import { FormControl, FormLabel } from '@chakra-ui/form-control';

import { MdArrowDropDown, MdArrowDropUp } from 'react-icons/md';

import { AnimatePresence, motion } from 'framer-motion';

import { useField } from 'formik';

import { days } from 'constants/weekdays';

import { FormikMultiSelectProps } from './FormikMultiSelect.props';

const MotionFlex = motion(Flex);

const daysMap: Record<string, string> = {
  Monday: 'Mo',
  Tuesday: 'Tu',
  Wednesday: 'We',
  Thursday: 'Th',
  Friday: 'Fr',
  Saturday: 'Sa',
  Sunday: 'Su',
};

const FormikMultiSelect: FC<FormikMultiSelectProps> = (props) => {
  const {
    name,
    label,
    options,
    placeholder = '',
    labelProps = {},
    containerProps = {},
  } = props;

  const flexRef = useRef<any>(null);

  const [field, meta, helpers] = useField<string[]>(name);

  const fieldValue = Array.isArray(field.value) ? field.value : [];

  const { isOpen, onClose, onToggle } = useDisclosure();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (flexRef.current && !flexRef.current?.contains(e?.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [flexRef, onClose]);

  const value = fieldValue.map((i) => daysMap[i]).join(', ');

  return (
    <FormControl mt={3} {...containerProps} position="relative">
      <FormLabel
        htmlFor={name}
        fontSize="12px"
        color="#afafaf"
        fontWeight="bold"
        textTransform="capitalize"
        {...labelProps}>
        {label}
      </FormLabel>
      <Flex position="relative">
        <InputGroup
          onClick={() => {
            if (props.isReadOnly) {
              return;
            }

            onToggle();
            helpers.setTouched(true);
          }}
          zIndex={2}>
          <Input
            placeholder={placeholder}
            isReadOnly
            cursor="default"
            value={value}
          />
          <InputRightElement>
            {isOpen ? (
              <MdArrowDropUp size="20px" />
            ) : (
              <MdArrowDropDown size="20px" />
            )}
          </InputRightElement>
        </InputGroup>
        <AnimatePresence>
          {isOpen && (
            <MotionFlex
              ref={flexRef}
              p={3}
              bg="#fff"
              top="90%"
              left={0}
              right={0}
              zIndex={1}
              shadow="md"
              pos="absolute"
              direction="column"
              borderBottomRadius="10px"
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}>
              <SimpleGrid columns={2} spacing={3}>
                {options.map((o) => (
                  <Checkbox
                    colorScheme="primary"
                    borderColor="primary.500"
                    isChecked={fieldValue.includes(o)}
                    onChange={({ target }) => {
                      if (target.checked) {
                        const sorted = days.filter((i) =>
                          [...fieldValue, o].includes(i),
                        );

                        helpers.setValue(sorted);
                      } else {
                        helpers.setValue(field.value.filter((i) => i !== o));
                      }
                    }}>
                    {o}
                  </Checkbox>
                ))}
              </SimpleGrid>
            </MotionFlex>
          )}
        </AnimatePresence>
      </Flex>
      {meta.error && meta.touched && (
        <Text color="red.500" fontSize="sm">
          {meta.error}
        </Text>
      )}
    </FormControl>
  );
};

export default FormikMultiSelect;
